<template>
  <v-container class="my-n2" fluid>
    <v-card>
      <v-card-title>
        <div class="d-flex align-center">
          <v-icon color="primary" class="mr-2">mdi-file-document-multiple-outline</v-icon>

          Documentos por Programa
        </div>

        <v-spacer></v-spacer>
      </v-card-title>

      <v-card-text>
        <v-row dense>
          <v-col cols="12" sm="12" md="6" lg="4" xl="3">
            <v-text-field
              v-model="programaId"
              type="number"
              min="1"
              step="1"
              label="ID. del Programa"
              placeholder="Ingrese ID del programa"
              dense
              outlined
              clearable
              hide-details
              @click:clear="
                datos = [];
                consultaRealizada = false;
              "></v-text-field>
          </v-col>

          <v-col cols="12" sm="12" md="6" lg="3" xl="2">
            <v-btn
              class="ma-1"
              color="primary"
              :disabled="cargando || !programaId"
              :loading="cargando"
              @click="cargarDatos">
              <v-icon left>mdi-magnify</v-icon>
              Buscar
            </v-btn>

            <v-btn
              class="ma-1"
              color="success"
              :disabled="!programaId || descargandoExcel || datosFiltrados.length === 0"
              :loading="descargandoExcel"
              @click="descargarExcel">
              <v-icon>mdi-file-excel</v-icon>
            </v-btn>
          </v-col>
        </v-row>

        <v-divider class="my-2"></v-divider>

        <v-row dense>
          <v-col cols="12" sm="12" md="6" lg="4" xl="3">
            <v-text-field
              v-model="busqueda"
              label="Busqueda en tabla"
              placeholder="Buscar por CI, nombres o apellidos en la tabla"
              :disabled="datosFiltrados.length === 0"
              dense
              outlined
              clearable
              hide-details></v-text-field>
          </v-col>
        </v-row>
      </v-card-text>

      <v-progress-linear v-if="cargando" indeterminate color="primary"></v-progress-linear>

      <div v-if="datosFiltrados.length > 0" class="table-responsive">
        <v-simple-table dense fixed-header>
          <thead>
            <tr>
              <th colspan="6" class="text-center header-fijo">
                {{ datosFiltrados[0].idEsam }} - {{ datosFiltrados[0].programa }}
              </th>
              <th
                v-for="proposito in propositosOrdenados"
                :key="proposito"
                :colspan="tiposDocumentosPorProposito[proposito].length"
                class="text-center header-proposito"
                :class="`header-proposito-${proposito}`">
                {{ categoriasLabels[proposito] || proposito }}
              </th>
            </tr>

            <tr>
              <th class="text-center header-fijo">CI</th>
              <th class="text-center header-fijo">Apellidos</th>
              <th class="text-center header-fijo">Nombres</th>
              <th class="text-center header-fijo">Cod. Prog. Universidad</th>
              <th class="text-center header-fijo">ID Inscripcion</th>
              <th class="text-center header-fijo">Estado</th>
              <template v-for="proposito in propositosOrdenados">
                <th
                  v-for="tipoDoc in tiposDocumentosPorProposito[proposito]"
                  :key="`${proposito}-${tipoDoc}`"
                  style="min-width: 200px"
                  class="text-center header-tipo-documento"
                  :class="`header-proposito-${proposito}`">
                  <v-tooltip bottom max-width="300">
                    <template v-slot:activator="{ on, attrs }">
                      <span v-bind="attrs" v-on="on">{{ tipoDoc }}</span>
                    </template>

                    <div style="white-space: normal; word-break: break-word">
                      {{ tipoDoc }}
                    </div>
                  </v-tooltip>
                </th>
              </template>
            </tr>
          </thead>

          <tbody>
            <tr v-for="(item, index) in datosFiltrados" :key="index">
              <td class="text-no-wrap">{{ item.ci }}</td>
              <td class="text-no-wrap">{{ item.apellidos }}</td>
              <td class="text-no-wrap">{{ item.nombres }}</td>
              <td class="text-no-wrap">{{ item.codigoProgramaUniversidad }}</td>
              <td class="text-center">{{ item.idInscripcion }}</td>
              <td class="text-center">
                <v-chip :color="item.estadoInscripcion === 'inscrito' ? 'success' : 'grey'" x-small dark>
                  {{ item.estadoInscripcion }}
                </v-chip>
              </td>

              <template v-for="proposito in propositosOrdenados">
                <td
                  v-for="tipoDoc in tiposDocumentosPorProposito[proposito]"
                  :key="`${index}-${proposito}-${tipoDoc}`"
                  class="text-center">
                  <template v-if="obtenerDocumento(item, proposito, tipoDoc)">
                    <v-tooltip bottom max-width="300">
                      <template v-slot:activator="{ on, attrs }">
                        <v-chip
                          v-bind="attrs"
                          v-on="on"
                          :color="obtenerColorDocumento(obtenerDocumento(item, proposito, tipoDoc))"
                          x-small
                          dark>
                          {{ obtenerDocumento(item, proposito, tipoDoc).estadoRevision || 'N/A' }}
                        </v-chip>
                      </template>

                      <div>
                        <div>
                          <strong>Subido:</strong> {{ obtenerDocumento(item, proposito, tipoDoc).subidoDocumento }}
                        </div>
                        <div>
                          <strong>Estado:</strong>
                          {{ obtenerDocumento(item, proposito, tipoDoc).estadoRevision || 'N/A' }}
                        </div>
                        <div v-if="obtenerDocumento(item, proposito, tipoDoc).fechaVencimiento">
                          <strong>Vencimiento:</strong>
                          {{ formatearFechaSoloFecha(obtenerDocumento(item, proposito, tipoDoc).fechaVencimiento) }}
                        </div>
                        <div v-if="obtenerDocumento(item, proposito, tipoDoc).observaciones">
                          <strong>Obs:</strong> {{ obtenerDocumento(item, proposito, tipoDoc).observaciones }}
                        </div>
                      </div>
                    </v-tooltip>
                  </template>

                  <span v-else class="grey--text">-</span>
                </td>
              </template>
            </tr>
          </tbody>
        </v-simple-table>
      </div>

      <v-row v-else class="my-4">
        <v-col class="text-center">
          <v-icon size="48" color="grey">mdi-file-document-outline</v-icon>
          <div class="mt-2 grey--text">No se encontraron datos para mostrar.</div>
        </v-col>
      </v-row>
    </v-card>

    <notifications group="notifications" position="top right" />
  </v-container>
</template>

<script>
import { mapGetters } from 'vuex';
import inscripcionesDocumentosPersonasExcelService from '../../comisiones/services/inscripciones-documentos-personas-excel.service';
import inscripcionesDocumentosPersonasService from '../../comisiones/services/inscripciones-documentos-personas.service';

const ORDEN_PROPOSITOS = ['inscripcion', 'titulacion', 'emitido', 'recepcion'];

export default {
  name: 'DocumentosPorPrograma',
  data() {
    return {
      programaId: null,
      busqueda: '',
      datos: [],
      cargando: false,
      consultaRealizada: false,
      categoriasLabels: {
        inscripcion: 'Inscripcion',
        titulacion: 'Titulacion',
        emitido: 'Emitido',
        recepcion: 'Recepcion',
      },
      descargandoExcel: false,
    };
  },
  computed: {
    tiposDocumentosPorProposito() {
      const mapa = {};

      this.datos.forEach((item) => {
        if (!item.documentos) return;

        Object.keys(item.documentos).forEach((proposito) => {
          if (!mapa[proposito]) {
            mapa[proposito] = new Set();
          }

          item.documentos[proposito].forEach((doc) => {
            mapa[proposito].add(doc.tipoDocumentoPersona);
          });
        });
      });

      const resultado = {};

      Object.keys(mapa).forEach((proposito) => {
        resultado[proposito] = Array.from(mapa[proposito]);
      });

      return resultado;
    },
    propositosOrdenados() {
      const disponibles = Object.keys(this.tiposDocumentosPorProposito);

      return ORDEN_PROPOSITOS.filter((p) => disponibles.includes(p));
    },
    datosFiltrados() {
      if (!this.busqueda) {
        return this.datos;
      }

      const termino = this.busqueda.toLowerCase();

      return this.datos.filter(
        (item) =>
          (item.ci && item.ci.toLowerCase().includes(termino)) ||
          (item.apellidos && item.apellidos.toLowerCase().includes(termino)) ||
          (item.nombres && item.nombres.toLowerCase().includes(termino)),
      );
    },
    ...mapGetters({
      permisosDNA: 'comision/permisosDNA',
    }),
  },
  methods: {
    async cargarDatos() {
      if (!this.programaId) return;

      this.datos = [];
      this.cargando = true;
      this.consultaRealizada = true;

      const params = new URLSearchParams();

      if (this.$store.state.CARGO.sede?.id && !this.permisosDNA) {
        params.append('sedeId', this.$store.state.CARGO.sede.id);
      }

      try {
        const response = await inscripcionesDocumentosPersonasService.documentosPorProgramaId(this.programaId, {
          params,
        });
        const data = response.data;

        this.datos = data.datos || [];

        this.$notify({
          group: 'notifications',
          title: 'Exito',
          text: data.mensaje || 'Datos cargados correctamente',
          type: 'success',
        });
      } catch (error) {
        const response = error.response;
        const data = response?.data;
        const mensajeError = data?.mensaje || 'Ocurrio un error al cargar los datos';

        window.console.error('Error al cargar documentos por programa:', error);

        this.$notify({
          group: 'notifications',
          title: 'Error',
          text: mensajeError,
          type: 'error',
        });
      } finally {
        this.cargando = false;
      }
    },
    obtenerDocumento(item, proposito, tipoDocumento) {
      if (!item.documentos || !item.documentos[proposito]) return null;

      return item.documentos[proposito].find((doc) => doc.tipoDocumentoPersona === tipoDocumento) || null;
    },
    obtenerColorDocumento(doc) {
      if (!doc) return 'grey';

      if (doc.subidoDocumento === 'NO SUBIÓ') return 'grey';

      const colores = {
        pendiente: 'orange',
        aprobado: 'success',
        rechazado: 'error',
      };

      return colores[doc.estadoRevision] || 'blue';
    },
    obtenerTextoCorto(doc) {
      if (!doc) return '-';

      if (doc.subidoDocumento === 'NO SUBIÓ') return 'No';

      if (doc.estadoRevision) {
        const textos = {
          pendiente: 'Pend.',
          aprobado: 'Aprob.',
          rechazado: 'Rech.',
        };

        return textos[doc.estadoRevision] || 'Si';
      }

      return 'Si';
    },
    formatearFechaSoloFecha(fecha) {
      if (!fecha) {
        return '-';
      }

      return new Date(fecha).toLocaleDateString('es-BO', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      });
    },
    async descargarExcel() {
      if (!this.programaId) return;

      this.descargandoExcel = true;

      const params = new URLSearchParams();

      if (this.$store.state.CARGO.sede?.id && !this.permisosDNA) {
        params.append('sedeId', this.$store.state.CARGO.sede.id);
      }

      try {
        const response = await inscripcionesDocumentosPersonasExcelService.documentosPorProgramaId(this.programaId, {
          params,
        });

        const blob = new Blob([response.data], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `documentos_programa_${this.programaId}.xlsx`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } catch (error) {
        window.console.error('Error al descargar Excel:', error);

        this.$notify({
          group: 'notifications',
          title: 'Error',
          text: 'Ocurrio un error al descargar el archivo Excel.',
          type: 'error',
        });
      } finally {
        this.descargandoExcel = false;
      }
    },
  },
};
</script>

<style scoped>
.table-responsive {
  overflow-x: auto;
}

.header-fijo {
  background-color: #f5f5f5 !important;
  white-space: nowrap;
  border-right: 1px solid #e0e0e0 !important;
}

.header-proposito {
  white-space: nowrap;
  border-left: 2px solid #bdbdbd !important;
  border-right: 2px solid #bdbdbd !important;
}

.header-tipo-documento {
  white-space: nowrap;
  font-size: 11px !important;
  max-width: 120px;
  overflow: hidden;
  text-overflow: ellipsis;
}

.header-proposito-inscripcion {
  background-color: #e3f2fd !important;
}

.header-proposito-titulacion {
  background-color: #fff3e0 !important;
}

.header-proposito-emitido {
  background-color: #e8f5e9 !important;
}

.header-proposito-recepcion {
  background-color: #fce4ec !important;
}

.celda-larga {
  max-width: 300px;
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>
